import { Images } from '@/assets/images';
import { useMergingState } from '@/hooks';
import { MenuContext } from '@/routers/main-layout';
import {
	forwardRef,
	MouseEvent,
	SyntheticEvent,
	useCallback,
	useContext,
	useEffect,
	useImperativeHandle,
	useRef,
} from 'react';
import { Resizable, ResizableProps, ResizeCallbackData } from 'react-resizable';

type ResizableBoxWithHandleProps = Omit<ResizableProps, 'width'> & {
	width?: number;
	height?: number;
};

const ResizableBoxWithHandle = forwardRef<
	HTMLDivElement | any,
	ResizableBoxWithHandleProps
>(
	(
		{ children, width = 220, height = window.innerHeight - 70, ...props },
		ref
	) => {
		const { menuOpen, onToggle } = useContext(MenuContext);

		const [size, setSize] = useMergingState({ width, height });
		const resizableRef = useRef<HTMLDivElement | null>(null);

		useEffect(() => {
			if (menuOpen) {
				setSize({ width });
			} else {
				setSize({ width: 0 });
			}
		}, [menuOpen]);

		useImperativeHandle(ref, () => ({
			resetSize: () => {
				setSize({ width, height });
			},
		}));

		const handleResize = (e: SyntheticEvent, { size }: ResizeCallbackData) => {
			setSize(size);
		};

		const onMouseMove = useCallback(
			(e: globalThis.MouseEvent) => {
				e.preventDefault();

				if (!resizableRef.current || size.width === 0) return;
				document.body.style.cursor = 'w-resize';

				const rect = resizableRef.current.getBoundingClientRect();

				const newWidth = e.clientX - rect.left;
				// const newHeight = e.clientY - rect.top;

				setSize({
					width: Math.min(newWidth, window.innerWidth / 2),
					// height: Math.min(newHeight, window.innerHeight / 2),
				});
			},
			[resizableRef.current]
		);

		const onIconResizableMouseDown = useCallback(
			(e: MouseEvent<HTMLImageElement>) => {
				e.preventDefault();

				const onMouseUp = (ev: globalThis.MouseEvent) => {
					document.body.style.cursor = 'auto';
					if (ev.clientX <= 100) {
						onToggle();
					}
					document.removeEventListener('mousemove', onMouseMove);
					document.removeEventListener('mouseup', onMouseUp);
				};

				document.addEventListener('mousemove', onMouseMove);
				document.addEventListener('mouseup', onMouseUp);
			},
			[onMouseMove]
		);

		const onResizeStart = useCallback((e: SyntheticEvent) => {
			document.body.style.cursor = 'w-resize';
		}, []);

		const onResizeStop = useCallback((e: SyntheticEvent) => {
			document.body.style.cursor = 'auto';
		}, []);

		return (
			<div className="relative hidden lg:block">
				<Resizable
					width={size.width}
					height={size.height}
					onResize={handleResize}
					onResizeStart={onResizeStart}
					onResizeStop={onResizeStop}
					resizeHandles={['e']}
					minConstraints={[0, 0]}
					maxConstraints={[window.innerWidth / 2, window.innerHeight / 2]}
					{...{ props }}
				>
					<div
						ref={resizableRef}
						style={{
							width: size.width,
							height: '100%',
						}}
					>
						{children}
					</div>
				</Resizable>

				<img
					src={Images.iconMenuResizable}
					className="min-w-4 min-h-4 cursor-w-resize absolute top-1/2 -translate-y-1/2 right-0"
					onMouseDown={onIconResizableMouseDown}
				/>
			</div>
		);
	}
);

ResizableBoxWithHandle.displayName = 'ResizableBoxWithHandle';

export default ResizableBoxWithHandle;
